import { useContext, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import Accordion from "../atoms/accordion";
import Button from "../atoms/button";
import Loading from "../atoms/loading";
import Modal from "../utils/modal";
import ToastContext from "../../context/toastProvider";
import MobileFilterSheet from "../../mobile/ui/MobileFilterSheet";
import MobileFormField from "../../mobile/ui/MobileFormField";
import { getProductsByCheckoutGroup, updateProductStatus, getCheckoutSandboxTools, simulateCheckoutPixApproval } from "./functions";
import {
  getGradingTypeLabel,
  priorityTypes,
} from "../utils/gradingTypes";
import { getNextStatus, statusObject } from "../utils/allProductStatus";
import { cashnator, formatarData } from "../utils/functions";
import {
  formatProductShipping,
  packagePanelLabel,
  shippingSharedLabel,
} from "../utils/formatProductShipping";

const asNumberCents = (value) => {
  if (value == null || value === "") return null;
  const n = Number(value);
  if (!Number.isFinite(n) || n < 0) return null;
  return Math.round(n);
};

const inputClass =
  "w-full rounded-lg border border-primary/40 bg-monao px-3 py-2 text-center text-laranjinha";

/** Célula de resumo — mesmo ritmo visual da ficha do pedido (label + valor). */
const MetaCell = ({ label, value, hint, isMobile }) => {
  if (value == null || value === "") return null;
  return (
    <div
      className={
        isMobile
          ? "min-w-0 flex-1 basis-[calc(50%-0.5rem)] rounded-lg border border-primary/30 px-3 py-2"
          : "min-w-[9rem] max-w-[16rem] flex-1 text-center"
      }
    >
      <p
        className={
          isMobile
            ? "m-0 text-xs text-laranjinha/60"
            : "m-0 text-sm text-[#999] -mb-1"
        }
      >
        {label}
      </p>
      <p
        className={
          isMobile
            ? "m-0 mt-0.5 break-all text-sm font-medium text-laranjinha"
            : "m-0 mt-1 break-all text-base"
        }
        title={typeof value === "string" ? value : undefined}
      >
        {value}
      </p>
      {hint ? (
        <p
          className={
            isMobile
              ? "m-0 mt-1 text-xs text-primary"
              : "m-0 mt-1 text-xs text-[#b47c06]"
          }
        >
          {hint}
        </p>
      ) : null}
    </div>
  );
};

/**
 * Agrupamento do Checkout V2 — Accordion + resumo + alteração de status via popup/sheet.
 */
const CheckoutGroupPanel = ({
  token,
  productId,
  productStatus,
  checkoutSessionId,
  asaasPaymentId,
  productShipping,
  productInsurance,
  shippingShared,
  checkoutItemCount,
  shippingChargedOnThisProduct,
  declaredValueOrderTotalCents,
  declaredValueAllocationMode,
  userEmail,
  onStatusUpdated,
  variant = "desktop",
}) => {
  const navigate = useNavigate();
  const { goodToast, badToast } = useContext(ToastContext);
  const [siblings, setSiblings] = useState([]);
  const [groupProducts, setGroupProducts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const [busy, setBusy] = useState(false);
  const [simulatePixAvailable, setSimulatePixAvailable] = useState(false);
  const [simulatingPix, setSimulatingPix] = useState(false);

  /** null | { mode: "single", product } | { mode: "bulk" } */
  const [statusTarget, setStatusTarget] = useState(null);
  const [status, setStatus] = useState("");
  const [notify, setNotify] = useState(true);
  const [returnTrackingCode, setReturnTrackingCode] = useState("");

  const reloadGroup = async ({ silent = false } = {}) => {
    if (!token || (!checkoutSessionId && !asaasPaymentId)) {
      setSiblings([]);
      setGroupProducts([]);
      return;
    }
    if (!silent) setLoading(true);
    setError(false);
    const data = await getProductsByCheckoutGroup(token, {
      checkoutSessionId,
      asaasPaymentId,
    });
    if (data?.type !== "success") {
      setError(true);
      setSiblings([]);
      setGroupProducts([]);
      if (!silent) setLoading(false);
      return;
    }
    const list = Array.isArray(data.data) ? data.data : [];
    setGroupProducts(list);
    setSiblings(list.filter((p) => String(p?._id) !== String(productId)));
    if (!silent) setLoading(false);
  };

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      if (!token || (!checkoutSessionId && !asaasPaymentId)) {
        setSiblings([]);
        setGroupProducts([]);
        return;
      }
      setLoading(true);
      setError(false);
      const data = await getProductsByCheckoutGroup(token, {
        checkoutSessionId,
        asaasPaymentId,
      });
      if (cancelled) return;
      if (data?.type !== "success") {
        setError(true);
        setSiblings([]);
        setGroupProducts([]);
        setLoading(false);
        return;
      }
      const list = Array.isArray(data.data) ? data.data : [];
      setGroupProducts(list);
      setSiblings(list.filter((p) => String(p?._id) !== String(productId)));
      setLoading(false);
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [token, productId, checkoutSessionId, asaasPaymentId]);

  useEffect(() => {
    let cancelled = false;
    const loadCapability = async () => {
      if (!token) {
        setSimulatePixAvailable(false);
        return;
      }
      const data = await getCheckoutSandboxTools(token);
      if (cancelled) return;
      setSimulatePixAvailable(data?.type === "success" && data?.data?.simulatePix === true);
    };
    loadCapability();
    return () => {
      cancelled = true;
    };
  }, [token]);

  const checkoutShippingDisplay = useMemo(() => {
    const fromSelf = formatProductShipping(productShipping, { checkoutSessionId });
    if (fromSelf) return fromSelf;
    for (const p of groupProducts) {
      const formatted = formatProductShipping(p?.shipping, {
        checkoutSessionId: p?.checkoutSessionId || checkoutSessionId,
      });
      if (formatted) return formatted;
    }
    return null;
  }, [productShipping, groupProducts, checkoutSessionId]);

  const packageGroups = useMemo(() => {
    const groups = new Map();
    groupProducts.forEach((product) => {
      const packageId = product?.shippingPackageId == null
        ? ""
        : String(product.shippingPackageId).trim();
      const key = packageId ? `package:${packageId}` : "legacy";
      if (!groups.has(key)) {
        groups.set(key, []);
      }
      groups.get(key).push(product);
    });
    return Array.from(groups, ([key, products]) => {
      const first = products[0] || {};
      const numberOfCards = products.reduce(
        (total, product) => total + (Number(product?.numberOfCards) || 0),
        0,
      );
      const shippingDisplay = first.delivery === "presencial"
        ? null
        : products
          .map((product) => formatProductShipping(product?.shipping, {
            checkoutSessionId: product?.checkoutSessionId || checkoutSessionId,
          }))
          .find(Boolean) || null;
      return {
        key,
        products,
        label: packagePanelLabel({ ...first, numberOfCards }),
        shippingDisplay,
      };
    });
  }, [groupProducts, checkoutSessionId]);

  const checkoutDeclaredDisplay = useMemo(() => {
    const fromProp = asNumberCents(declaredValueOrderTotalCents);
    if (fromProp != null) return cashnator(fromProp);
    for (const p of groupProducts) {
      const fromSibling = asNumberCents(p?.declaredValueOrderTotalCents);
      if (fromSibling != null) return cashnator(fromSibling);
    }
    return null;
  }, [declaredValueOrderTotalCents, groupProducts]);

  const showCheckoutDeclared = Boolean(
    checkoutDeclaredDisplay
    || declaredValueAllocationMode === "proportional_cards"
    || groupProducts.some((p) => p?.declaredValueAllocationMode === "proportional_cards"),
  );

  const checkoutInsuranceDisplay = useMemo(() => {
    if (groupProducts.length > 0) {
      const sum = groupProducts.reduce((acc, p) => {
        const n = asNumberCents(p?.insurance);
        return acc + (n != null ? n : 0);
      }, 0);
      return cashnator(sum);
    }
    const fromProp = asNumberCents(productInsurance);
    return fromProp != null ? cashnator(fromProp) : null;
  }, [groupProducts, productInsurance]);

  const sharedNote = shippingSharedLabel({
    shippingShared: shippingShared === true,
    checkoutItemCount: checkoutItemCount || (siblings.length > 0 ? siblings.length + 1 : undefined),
    shippingChargedOnThisProduct,
  });

  if (!checkoutSessionId && !asaasPaymentId) {
    return null;
  }

  const isMobile = variant === "mobile";
  const groupCount = groupProducts.length > 0
    ? groupProducts.length
    : (siblings.length > 0 ? siblings.length + 1 : (checkoutItemCount || 1));
  const accordionTitle = groupCount > 1
    ? `Mesmo checkout (${groupCount} lotes)`
    : "Mesmo checkout";

  const navigateToProduct = (targetId) => {
    const nextId = String(targetId || "");
    if (!nextId || nextId === String(productId)) return;
    navigate(`/manafix/products/${nextId}`);
  };

  const handleRoleta = (e, targetId) => {
    const nextId = String(targetId || "");
    if (!nextId) return;
    if (e.button === 1) {
      window.open(`/manafix/products/${nextId}`, "_blank");
    }
  };

  const closeStatusModal = ({ force = false } = {}) => {
    if (busy && !force) return;
    setStatusTarget(null);
    setStatus("");
    setNotify(true);
    setReturnTrackingCode("");
  };

  const openSingleStatus = (product) => {
    const isHQ = product?.store?.headquarter !== false;
    let next = getNextStatus(product?.delivery, product?.status, isHQ);
    if (product?.status === "ANALYZING") next = "ANALYZING";
    setStatus(next || product?.status || "");
    setNotify(true);
    setReturnTrackingCode(product?.returnTrackingCode || "");
    setStatusTarget({ mode: "single", product });
  };

  const openBulkStatus = () => {
    if (!groupProducts.length) return;
    const first = groupProducts[0];
    const isHQ = first?.store?.headquarter !== false;
    const next = getNextStatus(first?.delivery, first?.status, isHQ);
    setStatus(next || first?.status || "");
    setNotify(true);
    setReturnTrackingCode("");
    setStatusTarget({ mode: "bulk" });
  };

  const applyStatusToIds = async (ids, nextStatus, returnCode = "") => {
    if (!token || !nextStatus || !ids?.length) return { ok: 0, fail: 0, lastError: "" };
    let ok = 0;
    let fail = 0;
    let lastError = "";
    for (const id of ids) {
      const data = await updateProductStatus(
        token,
        id,
        nextStatus,
        notify,
        statusObject[nextStatus],
        returnCode,
        userEmail,
      );
      if (data?.type === "success") {
        ok += 1;
        setGroupProducts((prev) => prev.map((p) => (
          String(p?._id) === String(id) ? { ...p, status: nextStatus } : p
        )));
      } else {
        fail += 1;
        lastError = data?.message || "Falha ao atualizar status";
      }
    }
    return { ok, fail, lastError };
  };

  const confirmStatusChange = async () => {
    if (!statusTarget || !status) return;
    setBusy(true);
    const ids = statusTarget.mode === "bulk"
      ? groupProducts.map((p) => String(p?._id)).filter(Boolean)
      : [String(statusTarget.product?._id)];
    const returnCode = statusTarget.mode === "single" && status === "RETURNED"
      ? returnTrackingCode
      : "";
    const { ok, fail, lastError } = await applyStatusToIds(ids, status, returnCode);
    setBusy(false);
    if (ok > 0 && fail === 0) {
      goodToast?.(ok === 1 ? "Status do produto atualizado!" : `Status atualizado em ${ok} lotes!`);
      closeStatusModal({ force: true });
      onStatusUpdated?.();
      await reloadGroup({ silent: true });
      return;
    }
    if (ok > 0 && fail > 0) {
      badToast?.(`Atualizados ${ok}; falharam ${fail}. ${lastError}`);
      onStatusUpdated?.();
      await reloadGroup({ silent: true });
      return;
    }
    badToast?.(lastError || "Não foi possível atualizar o status");
  };

  const singleProduct = statusTarget?.mode === "single" ? statusTarget.product : null;
  const isBulk = statusTarget?.mode === "bulk";
  const singleUnchanged = Boolean(
    singleProduct
    && status === singleProduct?.status
    && returnTrackingCode === (singleProduct?.returnTrackingCode || ""),
  );
  const confirmDisabled = busy
    || !status
    || (isBulk ? false : singleUnchanged);

  const lotLabel = (p) => {
    const type = getGradingTypeLabel(p?.gradingType) || p?.gradingType || "Lote";
    const cards = p?.numberOfCards != null ? `${p.numberOfCards} itens` : null;
    const current = String(p?._id) === String(productId) ? "este pedido" : null;
    return [type, cards, current].filter(Boolean).join(" · ");
  };

  const statusHelpHint = (() => {
    if (isBulk) return null;
    if (!singleProduct) return null;
    const isHQ = singleProduct?.store?.headquarter !== false;
    if (singleProduct?.status === "ANALYZING") return null;
    const next = getNextStatus(singleProduct?.delivery, singleProduct?.status, isHQ);
    if (next) {
      return (
        <p className={isMobile ? "mb-4 text-center text-sm text-laranjinha/70" : "text-center"}>
          O status atual do pedido é{" "}
          <strong className="text-green-500">{statusObject[singleProduct?.status]}</strong>.
          {" "}O próximo status do fluxo seria{" "}
          <strong className="text-green-500">{statusObject[next]}</strong>,
          {" "}o qual já foi pré-selecionado na lista abaixo.
        </p>
      );
    }
    return (
      <p className={isMobile ? "mb-4 text-center text-sm text-laranjinha/70" : "text-center"}>
        O status atual do pedido é {statusObject[singleProduct?.status]}. Este é o status final do fluxo.
      </p>
    );
  })();

  const affectedLots = isBulk
    ? groupProducts
    : (singleProduct ? [singleProduct] : []);

  const statusChangeSummary = affectedLots.length > 0 ? (
    <div
      className={
        isMobile
          ? "mb-4 rounded-lg border border-primary/30 p-3"
          : "mb-4 mx-auto max-w-2xl rounded border border-primary/40 p-3"
      }
      data-testid="checkout-status-change-summary"
    >
      <p
        className={
          isMobile
            ? "m-0 mb-2 text-sm font-medium text-primary"
            : "m-0 mb-2 text-center text-sm text-primary"
        }
      >
        {isBulk
          ? `Resumo · ${affectedLots.length} lotes deste checkout`
          : "Resumo da alteração"}
      </p>
      <ul className={isMobile ? "m-0 space-y-2 p-0 list-none" : "m-0 space-y-2 p-0 list-none"}>
        {affectedLots.map((p) => {
          const fromLabel = statusObject[p?.status] || p?.status || "—";
          const toLabel = status
            ? (statusObject[status] || status)
            : "—";
          const unchanged = Boolean(status && status === p?.status);
          return (
            <li
              key={String(p?._id)}
              className={
                isMobile
                  ? "rounded-md border border-primary/20 px-2 py-2"
                  : "rounded border border-[#ffffff22] px-3 py-2"
              }
            >
              <p
                className={
                  isMobile
                    ? "m-0 text-sm font-medium text-laranjinha"
                    : "m-0 text-sm text-[#eee]"
                }
              >
                {lotLabel(p)}
              </p>
              <p
                className={
                  isMobile
                    ? "m-0 mt-1 text-xs text-laranjinha/70"
                    : "m-0 mt-1 text-sm text-[#bbb]"
                }
              >
                <span>{fromLabel}</span>
                <span className="mx-1 text-primary">→</span>
                <strong className={unchanged ? "text-[#888]" : "text-green-500"}>
                  {toLabel}
                </strong>
                {unchanged ? (
                  <span className="ml-1 text-[#888]">(sem mudança)</span>
                ) : null}
              </p>
            </li>
          );
        })}
      </ul>
    </div>
  ) : null;

  const statusFormFields = (
    <>
      {statusHelpHint}
      <div className={isMobile ? "" : "flex gap-2 w-fit m-auto my-4"}>
        {isMobile ? (
          <MobileFormField label="Novo status">
            <select
              className={inputClass}
              value={status}
              onChange={(e) => setStatus(e.target.value)}
              aria-label="Status do pedido"
              disabled={busy}
            >
              {Object.keys(statusObject).map((key) => (
                <option key={key} value={key}>{statusObject[key]}</option>
              ))}
            </select>
          </MobileFormField>
        ) : (
          <>
            <p>Novo status: </p>
            <select
              className="text-black text-center px-2"
              value={status}
              onChange={(e) => setStatus(e.target.value)}
              aria-label="Status do pedido"
              disabled={busy}
            >
              {Object.keys(statusObject).map((key) => (
                <option key={key} value={key}>{statusObject[key]}</option>
              ))}
            </select>
          </>
        )}
      </div>
      {statusChangeSummary}
      {!isBulk && status === "RETURNED" ? (
        isMobile ? (
          <MobileFormField label="Código de rastreio (retorno)">
            <input
              className={inputClass}
              type="text"
              value={returnTrackingCode}
              onChange={(e) => setReturnTrackingCode(e.target.value)}
              aria-label="Código de rastreio retorno"
              disabled={busy}
            />
          </MobileFormField>
        ) : (
          <div className="flex w-fit m-auto gap-2 my-4">
            <label>Código de rastreio (retorno)</label>
            <input
              type="text"
              className="text-black text-center"
              value={returnTrackingCode}
              onChange={(e) => setReturnTrackingCode(e.target.value)}
              aria-label="Código de rastreio retorno"
              disabled={busy}
            />
          </div>
        )
      ) : null}
      <label
        className={
          isMobile
            ? "mb-4 flex items-center gap-2 text-sm text-laranjinha/80"
            : "mb-4 w-fit m-auto block"
        }
      >
        <span className={isMobile ? "" : "mr-2"}>Notificar usuário: </span>
        <input
          type="checkbox"
          checked={notify}
          onChange={() => setNotify((v) => !v)}
          disabled={busy}
          aria-label="Notificar usuário"
        />
      </label>
    </>
  );

  const statusModalDesktop = statusTarget && !isMobile ? (
    <Modal width={isBulk ? "640px" : "50%"}>
      <div className="relative p-2" data-testid="checkout-group-status-modal">
        <a
          className="w-10 h-10 rounded-full border border-laranjinha cursor-pointer flex items-center justify-center font-bold absolute top-0 right-0 text-xl bg-primary text-black"
          href="/manafix/status"
          target="_blank"
          rel="noreferrer"
          title="Clique para ver a lista de status"
        >
          ?
        </a>
        <h3 className="m-0 mb-3 text-center text-lg text-primary">
          {isBulk ? "Alterar status de todos os lotes" : "Alterar status"}
        </h3>
        {statusFormFields}
        <div className="flex gap-2 w-fit m-auto">
          <Button
            classes="bg-green-500 hover:bg-green-300"
            disabled={confirmDisabled}
            click={confirmStatusChange}
          >
            {busy ? "Salvando…" : "Confirmar status"}
          </Button>
          <Button classes="bg-red-500 hover:bg-red-300" click={closeStatusModal} disabled={busy}>
            Cancelar
          </Button>
        </div>
      </div>
    </Modal>
  ) : null;

  const statusSheetMobile = isMobile ? (
    <MobileFilterSheet
      open={Boolean(statusTarget)}
      onClose={closeStatusModal}
      title={isBulk ? "Status de todos os lotes" : "Alterar status"}
    >
      <div data-testid="checkout-group-status-modal">
        {statusFormFields}
        <button
          type="button"
          disabled={confirmDisabled}
          onClick={confirmStatusChange}
          className="inline-flex min-h-[44px] w-full items-center justify-center rounded-lg bg-primary font-medium text-black hover:bg-[#b47c06] disabled:opacity-50"
        >
          {busy ? "Salvando…" : "Confirmar status"}
        </button>
      </div>
    </MobileFilterSheet>
  ) : null;

  const technicalIds = (
    <details
      className={
        isMobile
          ? "mt-3 text-xs text-laranjinha/45"
          : "mx-auto mt-3 max-w-3xl text-center text-xs text-[#666]"
      }
      data-testid="checkout-group-tech-ids"
    >
      <summary className={isMobile ? "cursor-pointer text-laranjinha/50" : "cursor-pointer text-[#777]"}>
        IDs técnicos (Asaas / sessão)
      </summary>
      <div
        className={
          isMobile
            ? "mt-2 space-y-1 break-all text-laranjinha/40"
            : "mt-2 space-y-1 break-all px-2 text-left text-[#777]"
        }
      >
        {asaasPaymentId ? <p className="m-0 font-mono">Asaas: {asaasPaymentId}</p> : null}
        {checkoutSessionId ? <p className="m-0 font-mono">Sessão: {checkoutSessionId}</p> : null}
      </div>
    </details>
  );

  const metaBlock = (
    <div className={isMobile ? "mb-4" : "mb-5"}>
      <p
        className={
          isMobile
            ? "mb-3 text-center text-sm text-laranjinha/70"
            : "m-0 mb-3 text-center text-sm text-[#ccc]"
        }
      >
        Lotes pagos juntos no mesmo carrinho
      </p>
      <div
        className={
          isMobile
            ? "flex flex-wrap gap-2"
            : "mx-auto flex max-w-4xl flex-wrap items-start justify-center gap-6"
        }
        data-testid="checkout-group-meta"
      >
        <MetaCell
          isMobile={isMobile}
          label="Lotes no checkout"
          value={String(groupCount)}
        />
        <MetaCell
          isMobile={isMobile}
          label="Frete do checkout"
          value={checkoutShippingDisplay || "—"}
          hint={sharedNote}
        />
        {showCheckoutDeclared ? (
          <MetaCell
            isMobile={isMobile}
            label="Valor declarado"
            value={checkoutDeclaredDisplay || "—"}
            hint="Total da compra (não rateado por lote)"
          />
        ) : null}
        <MetaCell
          isMobile={isMobile}
          label="Taxa de manuseio"
          value={checkoutInsuranceDisplay || "—"}
          hint={groupCount > 1 ? "Total do checkout (soma dos lotes)" : null}
        />
      </div>
      {(checkoutSessionId || asaasPaymentId) ? technicalIds : null}
    </div>
  );

  const handleSimulatePix = async () => {
    if (!token || !productId || simulatingPix || busy) return;
    setSimulatingPix(true);
    const data = await simulateCheckoutPixApproval(token, productId);
    setSimulatingPix(false);
    if (data?.type === "success") {
      goodToast?.("Confirmação PIX enviada à Asaas. Atualize em instantes se o status ainda não mudou.");
      await reloadGroup({ silent: true });
      if (typeof onStatusUpdated === "function") {
        onStatusUpdated();
      }
      return;
    }
    badToast?.(data?.message || "Não foi possível simular a aprovação do PIX");
  };

  const showSimulatePix = simulatePixAvailable
    && productStatus === "PIX"
    && Boolean(asaasPaymentId);

  const statusToolbar = (
    <div
      className={
        isMobile
          ? "mb-3 flex flex-col gap-2"
          : "mb-4 flex flex-wrap items-center justify-center gap-3"
      }
      data-testid="checkout-group-status-toolbar"
    >
      {showSimulatePix ? (
        <Button
          classes={
            isMobile
              ? "bg-transparent border border-primary text-primary hover:bg-primary/10 w-full"
              : "bg-transparent border border-primary text-primary hover:bg-primary/10"
          }
          disabled={simulatingPix || busy}
          click={handleSimulatePix}
        >
          {simulatingPix ? "Simulando…" : "Simular aprovação PIX"}
        </Button>
      ) : null}
      <Button
        classes={
          isMobile
            ? "bg-primary hover:bg-[#b47c06] w-full"
            : "bg-primary hover:bg-[#b47c06]"
        }
        disabled={!groupProducts.length || busy}
        click={openBulkStatus}
      >
        Alterar status de todos
      </Button>
    </div>
  );

  const renderPackageHeader = (packageGroup) => (
    <div
      className={
        isMobile
          ? "mb-2 rounded-lg border border-primary/30 px-3 py-2"
          : "mx-auto mb-3 max-w-4xl border-b border-primary/30 pb-2 text-center"
      }
      data-testid="checkout-package-header"
    >
      <p className={isMobile ? "m-0 text-sm font-medium text-primary" : "m-0 text-base text-primary"}>
        {packageGroup.label}
      </p>
      {packageGroup.shippingDisplay ? (
        <p className={isMobile ? "m-0 mt-1 text-xs text-laranjinha/60" : "m-0 mt-1 text-sm text-[#bbb]"}>
          Frete do checkout: {packageGroup.shippingDisplay}
        </p>
      ) : null}
    </div>
  );

  const lotsBody = (
    <>
      {loading ? (
        <div className="w-fit m-auto my-6">
          <Loading />
        </div>
      ) : null}
      {error ? (
        <p className="my-6 text-center text-red-400">
          Não foi possível carregar os lotes do checkout.
        </p>
      ) : null}
      {!loading && !error && groupProducts.length === 0 ? (
        <p className={isMobile ? "my-4 text-center text-laranjinha/60" : "my-6 text-center text-[#999]"}>
          Nenhum lote encontrado neste checkout.
        </p>
      ) : null}
      {!loading && !error && groupProducts.length > 0 && !isMobile ? (
        <div className="space-y-5">
          {packageGroups.map((packageGroup) => (
            <section key={packageGroup.key} data-testid="checkout-package-group">
              {renderPackageHeader(packageGroup)}
              <div className="relative overflow-x-auto">
                <table className="w-fit m-auto text-center block">
                  <thead className="border-b border-primary/40">
                    <tr>
                      <th className="w-40">Tipo</th>
                      <th className="w-24">Nº itens</th>
                      <th className="w-28">Prioridade</th>
                      <th className="w-36">Status</th>
                      <th className="w-28">Preço final</th>
                      <th className="w-28">Criado em</th>
                      <th className="w-44">Ações</th>
                    </tr>
                  </thead>
                  <tbody className="leading-10 w-full max-h-[350px] overflow-y-scroll block">
                    {packageGroup.products.map((p) => {
                      const isCurrent = String(p?._id) === String(productId);
                      return (
                        <tr
                          key={String(p?._id)}
                          className={`border-b border-[#ffffff33] ${isCurrent ? "bg-primary/10" : "hover:bg-[#ffffff33]"}`}
                          data-testid={isCurrent ? "checkout-group-current-row" : undefined}
                        >
                          <td className="w-40">
                            {getGradingTypeLabel(p?.gradingType)}
                            {isCurrent ? (
                              <span className="ml-1 text-xs text-primary">(este)</span>
                            ) : null}
                          </td>
                          <td className="w-24">{p?.numberOfCards}</td>
                          <td className="w-28">{priorityTypes[p?.priority] || p?.priority || "—"}</td>
                          <td className="w-36">{statusObject[p?.status] || p?.status || "—"}</td>
                          <td className="w-28">{cashnator(p?.finalPrice) || "—"}</td>
                          <td className="w-28">{p?.createdAt ? formatarData(p.createdAt) : "—"}</td>
                          <td className="w-44 py-2">
                            <div className="flex flex-row flex-wrap items-center justify-center gap-2">
                              <Button
                                classes="bg-primary hover:bg-[#b47c06] text-sm"
                                disabled={busy}
                                click={() => openSingleStatus(p)}
                              >
                                Alterar status
                              </Button>
                              {!isCurrent ? (
                                <Button
                                  onMouseDown={(e) => handleRoleta(e, p?._id)}
                                  classes="border border-primary/60 bg-transparent text-primary hover:bg-primary/20 text-sm"
                                  click={() => navigateToProduct(p?._id)}
                                >
                                  Abrir
                                </Button>
                              ) : null}
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </section>
          ))}
        </div>
      ) : null}
      {!loading && !error && groupProducts.length > 0 && isMobile ? (
        <div className="space-y-4" data-testid="checkout-group-siblings-mobile">
          {packageGroups.map((packageGroup) => (
            <section key={packageGroup.key} data-testid="checkout-package-group">
              {renderPackageHeader(packageGroup)}
              <ul className="space-y-2">
                {packageGroup.products.map((p) => {
                  const isCurrent = String(p?._id) === String(productId);
                  return (
                    <li
                      key={String(p?._id)}
                      className={`rounded-lg border px-3 py-2.5 ${isCurrent ? "border-primary bg-primary/5" : "border-primary/40"}`}
                    >
                      <div className="w-full text-left">
                        <p className="m-0 text-sm font-medium text-primary">
                          {getGradingTypeLabel(p?.gradingType) || p?.gradingType}
                          {p?.numberOfCards != null ? ` · ${p.numberOfCards} itens` : ""}
                          {isCurrent ? " · este pedido" : ""}
                        </p>
                        <p className="m-0 mt-1 text-xs text-laranjinha/70">
                          {[
                            priorityTypes[p?.priority] || p?.priority,
                            statusObject[p?.status] || p?.status,
                            cashnator(p?.finalPrice),
                          ]
                            .filter(Boolean)
                            .join(" · ")}
                        </p>
                        <div className="mt-3 flex flex-row gap-2">
                          <button
                            type="button"
                            disabled={busy}
                            onClick={() => openSingleStatus(p)}
                            className="inline-flex min-h-[40px] flex-1 items-center justify-center rounded-lg bg-primary px-3 text-sm font-medium text-black hover:bg-[#b47c06] disabled:opacity-50"
                          >
                            Alterar status
                          </button>
                          {!isCurrent ? (
                            <button
                              type="button"
                              className="inline-flex min-h-[40px] min-w-[5.5rem] items-center justify-center rounded-lg border border-primary/50 px-3 text-sm text-primary"
                              onClick={() => navigateToProduct(p?._id)}
                            >
                              Abrir
                            </button>
                          ) : null}
                        </div>
                      </div>
                    </li>
                  );
                })}
              </ul>
            </section>
          ))}
        </div>
      ) : null}
    </>
  );

  return (
    <div
      className={isMobile ? "mt-3" : "mt-4 w-full basis-full"}
      data-testid="checkout-group-panel"
    >
      <Accordion title={accordionTitle} defaultOpen>
        {metaBlock}
        {statusToolbar}
        <div
          className={
            isMobile
              ? "mb-2 border-t border-primary/20 pt-3"
              : "mb-3 border-t border-primary/30 pt-3"
          }
        >
          <h3
            className={
              isMobile
                ? "mb-2 text-center text-sm font-medium text-primary"
                : "mb-3 text-center text-base text-primary"
            }
          >
            {groupProducts.length > 0
              ? `Lotes deste checkout (${groupProducts.length})`
              : "Lotes deste checkout"}
          </h3>
          {lotsBody}
        </div>
      </Accordion>
      {statusModalDesktop}
      {statusSheetMobile}
    </div>
  );
};

export default CheckoutGroupPanel;
